<template>
	<div
		:class="[
			`collapsible collapsible--align-${alignment}`,
			{
				'collapsible--expanded': isOpen,
			},
		]"
		data-cy="collapsible"
	>
		<div
			class="collapsible__content"
			:class="{
				'collapsible__content--collapsed': !isOpen,
				'collapsible__content--expanded': isOpen,
			}"
			:style="contentStyle"
		>
			<div
				ref="content"
				class="collapsible__inner"
			>
				<content-watcher @loaded="setElementHeight">
					<slot />
				</content-watcher>
			</div>
		</div>

		<template v-if="elementHeight > height">
			<helio-button
				v-if="ctaType === 'button'"
				:class="[
					'collapsible__cta collapsible__cta--button',
					{
						'collapsible__cta--collapsed': !isOpen,
						'collapsible__cta--expanded': isOpen,
					}
				]"
				:text="text"
				:style="buttonStyle"
				color="primary"
				@click="toggle"
			/>

			<div
				v-else-if="ctaType === 'text'"
				:class="[
					'collapsible__cta collapsible__cta--text',
					{
						'collapsible__cta--collapsed': !isOpen,
						'collapsible__cta--expanded': isOpen,
					}
				]"
				:style="buttonStyle"
				@click="toggle"
			>
				<helio-icon
					class="collapsible__icon"
					token="angle-down"
				/>

				{{ text }}
			</div>
		</template>
	</div>
</template>

<script>
import ContentWatcher from '../content-watcher/ContentWatcher.vue';
import Icon from '../icon/Icon.vue';

export default {
	name: 'Collapsible',

	components: {
		ContentWatcher,
		'helio-icon': Icon,
	},

	props: {
		alignment: {
			type: String,
			required: false,
			default: 'left',
			validator: value => [
				'center',
				'left',
				'right',
			].indexOf(value) !== -1,
			description: 'Sets the horizontal alignment of the collapseText/expandText toggle link',
		},

		backgroundColor: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the colour that will appear in the background of the collapseText/expandText toggle link',
		},

		collapseText: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the text that users will click on to collapse the Collapsible component',
		},

		ctaType: {
			type: String,
			required: false,
			default: 'text',
			validator: value => [
				'text',
				'button',
			].indexOf(value) !== -1,
			description: 'Sets the default cta type for collapsing/uncollapsing the collapsible',
		},

		expandText: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the text that users will click on to expand the Collapsible component',
		},

		height: {
			type: Number,
			required: true,
			validator: height => height >= 0,
			description: 'Sets the height of the Collapsible component when it\'s in its collapsed state',
		},

		textColor: {
			type: String,
			required: false,
			default: '#202c40',
			description: 'Sets the colour of the text in the collapseText/expandText toggle link',
		},
	},

	slots: {
		default: {
			required: true,
			scoped: false,
			description: 'Supplies the content of the Collapsible component',
		},
	},

	data() {
		return {
			isOpen: false,
			elementHeight: 0,
		};
	},

	computed: {
		dynamicBackgroundColor() {
			if (!this.isOpen) {
				return this.backgroundColor.split(' ').join('');
			}

			return 'rgba(255, 255, 255, 0)';
		},

		buttonStyle() {
			if (this.backgroundColor || this.textColor) {
				return {
					backgroundImage: this.backgroundColor
						? `linear-gradient(to bottom, rgba(0, 0, 0, 0), ${this.dynamicBackgroundColor} 60%)`
						: null,
					color: this.textColor || null,
				};
			}

			return null;
		},

		contentStyle() {
			// If the collapsible is closed, we take the small height between
			// the elementHeight and the maximum height given as a prop
			const closedHeight = this.height > this.elementHeight ? this.elementHeight : this.height;

			return {
				height: !this.isOpen
					? `${closedHeight}px`
					: `${this.elementHeight}px`,
			};
		},

		text() {
			return !this.isOpen
				? this.expandText || this.$t('collapsible.show-more')
				: this.collapseText || this.$t('collapsible.show-less');
		},
	},

	mounted() {
		window.addEventListener('resize', this.onResize);
		this.onResize();
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		onResize() {
			this.setElementHeight();
		},

		setElementHeight() {
			setTimeout(() => {
				const { content } = this.$refs;

				if (content) {
					this.elementHeight = content.clientHeight;
				}

				this.$emit('resize');
			}, 150);
		},

		toggle() {
			this.isOpen = !this.isOpen;
			this.$emit(this.isOpen ? 'open' : 'close');
		},
	},
};
</script>

<style lang="scss" scoped src="./collapsible.scss" />
